interface StatCard {
  icon: string;
  label: string;
  value: string;
  sub: string;
  trend: "up" | "down" | "neutral";
  trendValue: string;
  gradient: string;
  iconBg: string;
}

interface StatsCardsProps {
  credit?: number | null;
  monthlySent?: number | null;
  successRate?: number | null;
  scheduledCount?: number | null;
  loading?: boolean;
  perMessageRate?: number | null;
}

export default function StatsCards({ credit, monthlySent, successRate, scheduledCount, loading, perMessageRate }: StatsCardsProps) {
  const safeCredit = credit ?? 0;
  const safeMonthlySent = monthlySent ?? 0;
  const safeSuccessRate = successRate ?? 0;
  const safeScheduledCount = scheduledCount ?? 0;
  const smsPossible = Math.floor(safeCredit / (perMessageRate ?? 20));

  const stats: StatCard[] = [
    {
      icon: "ri-wallet-3-line",
      label: "보유 크레딧",
      value: loading ? "..." : `${smsPossible.toLocaleString()}건`,
      sub: loading ? "" : `₩ ${safeCredit.toLocaleString()}`,
      trend: safeCredit < 5000 ? "down" : "neutral",
      trendValue: safeCredit < 5000 ? "충전 필요" : "충전",
      gradient: "from-violet-500/20 to-violet-600/10",
      iconBg: "from-violet-500 to-violet-600",
    },
    {
      icon: "ri-send-plane-2-line",
      label: "이번 달 발송",
      value: loading ? "..." : `${safeMonthlySent.toLocaleString()}건`,
      sub: "이번 달 누적",
      trend: "up",
      trendValue: `${safeMonthlySent > 0 ? "+" : ""}${safeMonthlySent.toLocaleString()}`,
      gradient: "from-pink-500/20 to-pink-600/10",
      iconBg: "from-pink-500 to-rose-500",
    },
    {
      icon: "ri-checkbox-circle-line",
      label: "발송 성공률",
      value: loading ? "..." : `${safeSuccessRate}%`,
      sub: "이번 달 기준",
      trend: safeSuccessRate >= 95 ? "up" : safeSuccessRate >= 80 ? "neutral" : "down",
      trendValue: `${safeSuccessRate}%`,
      gradient: "from-emerald-500/20 to-emerald-600/10",
      iconBg: "from-emerald-500 to-teal-500",
    },
    {
      icon: "ri-calendar-schedule-line",
      label: "예약 발송",
      value: loading ? "..." : `${safeScheduledCount}건`,
      sub: "대기 중",
      trend: "neutral",
      trendValue: safeScheduledCount > 0 ? `${safeScheduledCount}건 대기` : "없음",
      gradient: "from-amber-500/20 to-amber-600/10",
      iconBg: "from-amber-500 to-orange-500",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className={`relative bg-gradient-to-br ${stat.gradient} border border-white/[0.08] rounded-2xl p-5 overflow-hidden`}
        >
          <div className="flex items-start justify-between mb-4">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${stat.iconBg} shrink-0`}>
              <i className={`${stat.icon} text-white text-lg`}></i>
            </div>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                stat.trend === "up"
                  ? "bg-emerald-500/20 text-emerald-400"
                  : stat.trend === "down"
                  ? "bg-red-500/20 text-red-400"
                  : "bg-white/10 text-gray-400"
              }`}
            >
              {stat.trend === "up" && <i className="ri-arrow-up-line mr-0.5"></i>}
              {stat.trend === "down" && <i className="ri-arrow-down-line mr-0.5"></i>}
              {stat.trendValue}
            </span>
          </div>
          <p className="text-gray-400 text-xs mb-1">{stat.label}</p>
          {loading ? (
            <div className="h-8 w-24 bg-white/[0.06] rounded-lg animate-pulse mt-1"></div>
          ) : (
            <p className="text-white text-2xl font-bold leading-tight">{stat.value}</p>
          )}
          <p className="text-gray-500 text-xs mt-1">{stat.sub}</p>
        </div>
      ))}
    </div>
  );
}